// src/shared/components/ui/SearchBox.tsx
import React, { useEffect, useRef, FC } from 'react';

interface SearchBoxProps {
  /** Controlled input value */
  value: string;
  /** Called whenever the user edits the search text */
  onChange: (newVal: string) => void;
  /** Called when the user presses Enter while focused in this box */
  onEnter?: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Visual size of the control */
  size?: 'sm' | 'md';
  /** Optional extra classes for the container (e.g., to override max width) */
  className?: string;
}

const SearchBox: FC<SearchBoxProps> = ({
  value,
  onChange,
  onEnter,
  placeholder = 'Search…',
  size = 'md',
  className = '',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const isSm = size === 'sm';
  const rootCls = [
    'pac-glass-input flex items-center w-full max-w-xs rounded-md transition',
    isSm ? 'py-0.5' : '',
    className,
  ].filter(Boolean).join(' ');
  const iconWrapCls = isSm ? 'flex-shrink-0 pl-2' : 'flex-shrink-0 pl-3';
  const iconCls = isSm ? 'h-4 w-4 text-neutral-700' : 'h-5 w-5 text-neutral-700';
  const inputCls = [
    'flex-1 font-normal bg-transparent text-neutral-200 placeholder-neutral-700 italic placeholder:font-normal rounded-r-md focus:outline-none',
    isSm ? 'text-[12px] leading-5 py-1 pl-2 pr-2' : 'py-2 pl-2 pr-3',
  ].join(' ');

  // Funnel global keystrokes into this search box
  useEffect(() => {
    const handleGlobalKey = (e: KeyboardEvent) => {
      // Ignore modifier keys
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // If focus is already in a form element, skip
      const tag = (e.target as HTMLElement)?.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(tag)) return;
      // Handle printable chars and Backspace
      if (e.key.length === 1 || e.key === 'Backspace') {
        e.preventDefault();
        inputRef.current?.focus();
        const next = e.key === 'Backspace' ? value.slice(0, -1) : value + e.key;
        onChange(next);
      }
    };

    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [value, onChange]);

  // Local Enter handler
  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnter?.();
    }
  };

  return (
    <div className={rootCls}>
      <div className={iconWrapCls}>
        <svg
          className={iconCls}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={inputCls}
      />
    </div>
  );
};

export default SearchBox;
